import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CustomizationOption, UserImage } from '../../dto';
import { AbstractService } from '../abstractservice';
import { ApplicationService } from '../application.service';
import { JsonEnrichmentService } from '../jsonmapper.service';
import { PetService } from '../pet.service';

// Owned-only cosmetic lists for all three customization categories (avatars, profile
// backgrounds, pet backgrounds), one service rather than three near-identical ones - they share
// the same shape (a BehaviorSubject + init-guard, mirroring InventoryService's pattern, not
// UnifiedRepositoryGatewayService, which is for shared/global reference data) and the same
// tap-to-equip-with-revert-on-error flow. Kept as one class per category's method group rather
// than a single generic method, since each category's equip target/revert path is genuinely
// different (profile vs. per-pet cache) - forcing a shared abstraction there would obscure more
// than it'd save.
@Injectable({
    providedIn: 'root'
})
export class CustomizationService extends AbstractService {

    private readonly ownedAvatarsState: WritableSignal<UserImage[]> = signal<UserImage[]>([]);
    public readonly OwnedAvatars: Observable<UserImage[]> = toObservable(this.ownedAvatarsState);
    private avatarsInitialized = false;

    private readonly ownedProfileBackgroundsState: WritableSignal<CustomizationOption[]> = signal<CustomizationOption[]>([]);
    public readonly OwnedProfileBackgrounds: Observable<CustomizationOption[]> = toObservable(this.ownedProfileBackgroundsState);
    private profileBackgroundsInitialized = false;

    // Keyed per pet - each pet has its own owned-background collection, so a single global
    // signal doesn't fit. One lazily-created signal per petId, same init-guard idea as the other
    // two categories, just keyed by id instead of a single flag.
    private petBackgroundsByPetId = new Map<number, WritableSignal<CustomizationOption[]>>();
    private petBackgroundsInitializedPetIds = new Set<number>();

    constructor(private http: HttpClient, private petService: PetService, private coreAppService: ApplicationService, private jsonMapper: JsonEnrichmentService, private injector: Injector) {
        super(coreAppService.authService, coreAppService.featureFlagService);
    }

    // ---- Avatars ----

    public initializeOwnedAvatars(): void {
        if (!this.avatarsInitialized) {
            this.getOwnedAvatars().subscribe(owned => {
                this.avatarsInitialized = true;
                this.ownedAvatarsState.set(owned);
            });
        }
    }

    public getOwnedAvatars(): Observable<UserImage[]> {
        return this.http.get<UserImage[]>(environment.apiUrl + '/user/profile/images/options', this.httpOptionsAuthJson())
            .pipe(
                map(images => images.map(i => this.jsonMapper.map(i, UserImage))),
                retry(2),
                catchError(this.handleError)
            );
    }

    // Tap to equip: preview immediately, send the request, revert on failure.
    public equipAvatar(avatar: UserImage | null): void {
        const profile = this.coreAppService.userService.activeUserProfileSignal();
        if (!profile) { return; }

        const previous = profile.Image;
        profile.Image = avatar;
        this.coreAppService.userService.setActiveUserProfile(profile);

        this.http.put<void>(environment.apiUrl + '/user/profile/avatar', { Id: avatar?.Id ?? null }, this.httpOptionsAuthJson())
            .pipe(
                tap({
                    error: () => {
                        profile.Image = previous;
                        this.coreAppService.userService.setActiveUserProfile(profile);
                    }
                }),
                catchError(err => {
                    this.coreAppService.displayError(err.error, 'Could not change your avatar', null, null, null);
                    return this.handleError(err);
                })
            )
            .subscribe({ error: () => { /* already reverted and surfaced above */ } });
    }

    // ---- Profile backgrounds ----

    public initializeOwnedProfileBackgrounds(): void {
        if (!this.profileBackgroundsInitialized) {
            this.getOwnedProfileBackgrounds().subscribe(owned => {
                this.profileBackgroundsInitialized = true;
                this.ownedProfileBackgroundsState.set(owned);
            });
        }
    }

    public getOwnedProfileBackgrounds(): Observable<CustomizationOption[]> {
        return this.http.get<CustomizationOption[]>(environment.apiUrl + '/user/profile/background/options', this.httpOptionsAuthJson())
            .pipe(
                retry(2),
                catchError(this.handleError)
            );
    }

    // Tap to equip: preview immediately, send the request, revert on failure.
    public equipProfileBackground(background: CustomizationOption | null): void {
        const profile = this.coreAppService.userService.activeUserProfileSignal();
        if (!profile) { return; }

        const previous = profile.ProfileBackground;
        profile.ProfileBackground = background;
        this.coreAppService.userService.setActiveUserProfile(profile);

        this.http.put<void>(environment.apiUrl + '/user/profile/background', { Id: background?.Id ?? null }, this.httpOptionsAuthJson())
            .pipe(
                tap({
                    error: () => {
                        profile.ProfileBackground = previous;
                        this.coreAppService.userService.setActiveUserProfile(profile);
                    }
                }),
                catchError(err => {
                    this.coreAppService.displayError(err.error, 'Could not change your profile background', null, null, null);
                    return this.handleError(err);
                })
            )
            .subscribe({ error: () => { /* already reverted and surfaced above */ } });
    }

    // ---- Pet backgrounds ----

    private petBackgroundsStateFor(petId: number): WritableSignal<CustomizationOption[]> {
        let state = this.petBackgroundsByPetId.get(petId);
        if (!state) {
            state = signal<CustomizationOption[]>([]);
            this.petBackgroundsByPetId.set(petId, state);
        }
        return state;
    }

    // Called from component click handlers, not a field initializer or constructor - toObservable()
    // needs an injection context passed explicitly here since there's no ambient one at call time.
    public ownedPetBackgroundsFor(petId: number): Observable<CustomizationOption[]> {
        return toObservable(this.petBackgroundsStateFor(petId), { injector: this.injector });
    }

    public initializeOwnedPetBackgrounds(petId: number): void {
        if (!this.petBackgroundsInitializedPetIds.has(petId)) {
            this.getOwnedPetBackgrounds(petId).subscribe(owned => {
                this.petBackgroundsInitializedPetIds.add(petId);
                this.petBackgroundsStateFor(petId).set(owned);
            });
        }
    }

    public getOwnedPetBackgrounds(petId: number): Observable<CustomizationOption[]> {
        return this.http.get<CustomizationOption[]>(environment.apiUrl + `/pet/${petId}/background/options`, this.httpOptionsAuthJson())
            .pipe(
                retry(2),
                catchError(this.handleError)
            );
    }

    // Tap to equip: preview immediately, send the request, revert on failure.
    public equipPetBackground(petId: number, background: CustomizationOption | null): void {
        const pets = this.petService.activeUserPetsSignal();
        const petIdx = pets.findIndex(p => p.Id === petId);
        if (petIdx === -1) { return; }

        const previous = pets[petIdx].Background;
        pets[petIdx].Background = background;
        this.petService.refreshActiveUserPets();

        this.http.put<void>(environment.apiUrl + `/pet/${petId}/background`, { Id: background?.Id ?? null }, this.httpOptionsAuthJson())
            .pipe(
                tap({
                    error: () => {
                        pets[petIdx].Background = previous;
                        this.petService.refreshActiveUserPets();
                    }
                }),
                catchError(err => {
                    this.coreAppService.displayError(err.error, 'Could not change this pet\'s background', null, null, null);
                    return this.handleError(err);
                })
            )
            .subscribe({ error: () => { /* already reverted and surfaced above */ } });
    }
}
